import styled from 'styled-components'
import { Layer } from '../variable/spaceTime.types'
import ViewSelectTopBoxDesktop from './viewSelectTopBoxDesktop.component'
import { VariableWithConclusion } from '../variable/variables.typing'
import { ReportWithLayer } from './reports'
import { useMemo, useRef } from 'react'
import type { VariableCollectionsAndOptionalResult } from '../variableCollection/variableCollection.typing'

const Wrapper = styled.aside`
  position: absolute;
  top: ${({ theme }) => theme.padding(0.6)};
  left: ${({ theme }) => theme.padding(0.6)};

  display: flex;
  flex-direction: column;

  min-height: 80px;
  max-height: 100px;
  max-width: 600px;
  width: 50%;

  .search-button-wrapper {
    button {
      display: flex;
      flex-direction: column;
      background: transparent;
      box-shadow: none;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      justify-content: center;
      align-items: center;
      transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
      transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
      transition-property: background-color, box-shadow;
      text-align: left;
      font-size: 1.2rem;
      color: ${({ theme }) => theme.colors.primary};

      padding: 10px;
      margin: 0 5px 5px 1px;

      background-color: #fff;

      &:hover {
        background-color: #fff;
        box-shadow: ${({ theme }) => theme.shadow(2)};
        z-index: 1;
      }
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    top: 56px;
    width: auto;
    right: ${({ theme }) => theme.padding(0.6)};
    min-height: auto;
  }

  .layer {
    display: flex;
    flex: 1;
    border-radius: 8px;
    box-shadow: ${({ theme }) => theme.shadow(1)};
    margin-bottom: ${({ theme }) => theme.padding(0.3)};
    margin-bottom: calc(${({ theme }) => theme.padding(0.3)} + 1px);

    background-color: white;

    @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
      background-color: ${({ theme }) => theme.backdropBackground};
      -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
      backdrop-filter: ${({ theme }) => theme.backdropFilter};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints?.mobile}px) {
      width: 100%;
      min-width: 200px;
      z-index: 1;
      box-shadow: none;
      border-radius: 0px;
      background: transparent;
    }
  }
`

interface ViewSelectProps {
  variables: VariableWithConclusion[]
  baseLayers: Layer[]
  layers: Layer[]
  setBaseLayers: (layers: Layer[] | ((layers: Layer[]) => Layer[])) => void
  onChangeReport: (report: ReportWithLayer | null) => void
  stateId: number | null
  stateName: string | null
  subtitle?: string | null
  isDisabled: boolean
}

export default function ViewSelect({
  baseLayers,
  setBaseLayers,
  variables,
  onChangeReport,
  stateId,
  stateName,
  subtitle,
  isDisabled,
  layers,
}: ViewSelectProps) {
  const ref = useRef(null)

  const viewSelectLayerEntriesMap = useMemo(() => {
    const map: Layer[] = []
    baseLayers.forEach(layer => {
      if ((layer.variable as VariableCollectionsAndOptionalResult)?.isVariableCollection) {
        map.push(layer)
      }
    })

    layers.forEach(layer => {
      if (!layer?.isVariableCollection) {
        map.push(layer)
      }
    })
    return map
  }, [baseLayers, layers])

  return (
    <Wrapper ref={ref}>
      {viewSelectLayerEntriesMap.map((layer, index) => (
        <div className='layer' key={index}>
          <ViewSelectTopBoxDesktop
            variables={variables}
            layer={layer}
            onChangeReport={onChangeReport}
            onChangeLayer={nextLayer => {
              setBaseLayers(oldBaseLayers => {
                const oldBaseLayer = oldBaseLayers[index]

                if (!oldBaseLayer) {
                  return oldBaseLayers
                }

                const newBaseLayers = [...oldBaseLayers]
                newBaseLayers[index] =
                  typeof nextLayer === 'function' ? nextLayer(oldBaseLayer) : nextLayer

                return newBaseLayers
              })
            }}
            stateId={stateId}
            stateName={stateName}
            subtitle={subtitle}
            isDisabled={isDisabled}
          />
        </div>
      ))}
    </Wrapper>
  )
}
